// @flow
/** @jsx isolateComponent */

import { type AccountPlan, isolateComponent } from '@zapier/common-utils';
import {
  Button,
  Colors,
  Heading,
  Spacer,
  Text,
  Typography,
} from '@zapier/design-system';

import type { TrialExtensionReason } from '@zapier/billing';

const styles = {
  container: {
    textAlign: 'center',
    maxWidth: 600,
    margin: '0 auto',
  },

  h2: {
    ...Typography.header800,
    color: Colors.grey900,
    marginBottom: 20,
  },
};

const distillPlanFeatures = plan => {
  const numApps = plan.limits.premium_apps_limit;
  const numZaps = plan.limits.zap_limit;
  const premiumAppsText =
    numApps === -1
      ? 'unlimited Premium Apps'
      : `${Number(numApps).toLocaleString()} Premium Apps`;
  const zapsText =
    numZaps === -1
      ? 'unlimited Zaps'
      : `${Number(numZaps).toLocaleString()} Zaps`;
  const tasksText = `${Number(
    plan.limits.action_limit
  ).toLocaleString()} tasks`;
  return `${premiumAppsText}, ${zapsText}, and ${tasksText}!`;
};

const contentMap = ({ onClose, plan }) => ({
  trial: {
    bodyText: `You are now experiencing the Zapier Professional Plan free for the next 7 days! Take advantage of unlimited Premium apps, unlimited Zaps, and ${Number(
      1000
    ).toLocaleString()} tasks!`,
    buttons: [
      <Button casing="uppercase" key="back-to-zap" onClick={onClose}>
        Back to Zap
      </Button>,
    ],
    headingText: `Free Trial Started`,
    image: null,
    subtitleText: null,
  },
  upgrade: {
    bodyText: `You just upgraded to the Zapier ${plan.type ||
      plan.name} Plan. Take advantage of ${distillPlanFeatures(plan)}`,
    buttons: [
      <Button casing="uppercase" key="back-to-zap" onClick={onClose}>
        Back to Zap
      </Button>,
    ],
    headingText: `Account Upgraded!`,
    image: null,
    subtitleText: null,
  },
});

type Props = {
  onClose: () => void,
  plan: AccountPlan,
  reason: TrialExtensionReason,
};

const Celebrate = ({ onClose, plan, reason }: Props) => {
  const contentProps = contentMap({ onClose, plan })[reason];
  return (
    <div css={styles.container}>
      <Heading css={styles.h2}>{contentProps.headingText}</Heading>
      <Text tag="p" type="bodyText800">
        {contentProps.bodyText}
      </Text>
      <Spacer height={30} />
      <div>
        <Button
          casing="uppercase"
          hasShadow={true}
          key="back-to-zap"
          onClick={onClose}
        >
          Back to Zap
        </Button>
      </div>
    </div>
  );
};

export default Celebrate;
